import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'
import apiService from '@/services/api'

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  onFolderCreated: (folder: Folder) => void
  parentFolder?: Folder | null
}

export default function CreateFolderModal({
  isOpen,
  onClose,
  onFolderCreated,
  parentFolder,
}: CreateFolderModalProps) {
  const { t } = useAppTranslation(['files', 'common'])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setError(
        t('folders.folderName', { ns: 'files' }) + ' ' + t('messages.required', { ns: 'common' })
      )
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const newFolder = await apiService.createFolder({
        name: name.trim(),
        description: description.trim() || undefined,
        parent_id: parentFolder?.id,
      })

      onFolderCreated(newFolder)
      handleClose()
    } catch (err: any) {
      setError(err.response?.data?.error || t('folders.createFolderError', { ns: 'files' }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="brand.600"
        >
          {t('folders.createFolder', { ns: 'files' })}
          {parentFolder && (
            <span
              style={{ fontSize: '14px', fontWeight: 'normal', color: 'rgba(255,255,255,0.8)' }}
            >
              {' '}
              in {parentFolder.name}
            </span>
          )}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4}>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel>{t('folders.folderName', { ns: 'files' })}</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('folders.folderName', { ns: 'files' })}
                  autoFocus
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('labels.description', { ns: 'common' })}</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('messages.optional', { ns: 'common' })}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            borderBottomRadius="xl"
          >
            <Button variant="ghost" mr={3} onClick={handleClose}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isLoading}
              loadingText={t('status.saving', { ns: 'common' })}
            >
              {t('folders.createFolder', { ns: 'files' })}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
